import { State, StateInvoke } from '@people-science/state-machine';

import api, { createNamedError, FetchError } from '../../../api/api';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { ServicePlugin } from '../ServicePlugin';

export interface QualificationInvokeData {
  studyParticipantId: number;
  isQualified: boolean;
}

const DATE_STRING_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;

/**
 * Recursively parses ISO date strings in the response into native Date objects,
 * matching the parsing useApiPost applied before this work moved into a service
 * so downstream consumers keep seeing Date instances (e.g. `qualified`).
 */
const parseDateFields = (json: any): any => {
  if (json === null || json === undefined) return json;
  if (Array.isArray(json)) return json.map(item => parseDateFields(item));
  if (typeof json === 'object') {
    const newJson: { [key: string]: any } = {};
    Object.keys(json).forEach(key => {
      newJson[key] = parseDateFields(json[key]);
    });
    return newJson;
  }
  if (typeof json === 'string' && DATE_STRING_REGEX.test(json)) {
    return new Date(json);
  }
  return json;
};

/**
 * State-machine service for qualifying / disqualifying a study participant.
 * Ported from useQualifyStudyParticipant (useApiPost) into a signal-driven
 * service so the form state machine owns the async work and cancellation
 * (CD-207). Unlike the old useApiPost path — which could not abort its fetch —
 * this honors the AbortSignal the machine passes (state exit / unmount), so the
 * in-flight POST is cancelled instead of completing in the background.
 */
const qualificationService = async (
  _state: State<any> | undefined,
  invoke: StateInvoke[],
  { signal }: { signal: AbortSignal }
): Promise<StudyParticipant> => {
  const data = invoke[0]?.data as QualificationInvokeData | undefined;
  const studyParticipantId = data?.studyParticipantId;

  if (!studyParticipantId || studyParticipantId <= 0) {
    throw {
      statusCode: 400,
      error: 'BadRequest',
      message: 'Study participant is not available yet.'
    } as FetchError;
  }

  if (signal.aborted) throw createNamedError('AbortError', 'Aborted');

  const path = `study-participants/${studyParticipantId}/qualify`;
  const body = { isQualified: !!data?.isQualified };

  const { response, abort } = api.post(path, body);
  const onAbort = () => abort();
  signal.addEventListener('abort', onAbort, { once: true });

  let result: Response;
  try {
    result = await response;
  } finally {
    signal.removeEventListener('abort', onAbort);
  }

  let json: any;
  try {
    json = await result.json();
  } catch {
    // Empty or non-JSON bodies (e.g. ALB 502 HTML error pages) — surface a
    // structured error instead of a raw parse error so the plugin can display
    // a useful failure (CD-136/149 family).
    throw {
      statusCode: result.status,
      message: result.statusText || 'Invalid response body'
    } as FetchError;
  }

  if (!result.ok) {
    throw json;
  }

  if (signal.aborted) throw createNamedError('AbortError', 'Aborted');

  return parseDateFields(json) as StudyParticipant;
};

export const QualificationServicePlugin: ServicePlugin = {
  type: 'qualification',
  service: qualificationService
};
